import { styled } from '@mui/material/styles';
import { useParams } from 'react-router';
import MarketHeader from './MarketHeader';
import FileExplorer from '@/components/FileExplorer/FileExplorer';
import { useState, useRef, useEffect, useMemo } from 'react';
import { ComplianceDocument } from '@/lib/types';
import { FilterSchema } from '@/components/ResourcesSidebar/ResourcesSidebarHelpers';
import {
  fetchComplianceDocuments,
  fetchComplianceDocumentsByFolder,
  fetchComplianceDocumentsFolderStructure,
  fetchFilters,
} from '@/lib/api';
import { useFileExplorerData } from '@/hooks/useFileExplorerData';
import {
  assetQueryKeys,
  invalidateAssetQueries,
} from '@/lib/assetQueryKeys';
import Button from '@/components/Button/Button';
import { FormattedMessage } from 'react-intl';
import UploadIcon from '@/assets/upload.svg?react';
import FilterIcon from '@/assets/filter.svg?react';
import Popper from '@/components/Popper/Popper';
import FileExplorerComplianceFilters from '@/components/FileExplorer/FileExplorerComplianceFilters';
import { flattenDeep } from 'lodash';
import ComplianceDocumentsUploadModal from '@/components/ComplianceDocumentsUploadModal/ComplianceDocumentsUploadModal';
import { FileTypesComplianceMarket } from '@/constants/FileTypesCompliance';
import { DropZone } from '@/components/DropZone/DropZone';
import Breadcrumbs from './Breadcrumbs';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNotFoundRedirect } from '@/hooks/useNotFoundRedirect';
import { AppFilters } from '@/lib/types';
import { Mode } from '@/components/FileExplorer/FileExplorerHelpers';
import { NavigationHeight } from '@/components/Navigation/NavigationHelpers';
import { useComplianceDocumentBulkActions } from '@/hooks/useComplianceDocumentBulkActions';
import RemoveFilesConfirmModal from '@/components/RemoveFilesConfirmModal/RemoveFilesConfirmModal';
import { mapFiltersToParams } from '@/pages/Resources/ResourcePageHelpers';

const MarketAdminPage = () => {
  const params = useParams();
  const marketId = params.marketId;
  const [path, setPath] = useState<string[]>([]);
  const [currentView, setCurrentView] = useState<Mode>('grid');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const filterRef = useRef<HTMLButtonElement>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [droppedFile, setDroppedFile] = useState<File | null>(null);
  const [filters, setFilters] = useState<Partial<FilterSchema>>({
    market: marketId ? [Number(marketId)] : [],
  });
  const queryClient = useQueryClient();

  const filtersQuery = useQuery<AppFilters>({
    queryKey: ['filters'],
    queryFn: fetchFilters,
    enabled: !!marketId,
    retry: false,
  });

  useNotFoundRedirect({
    queryResult: filtersQuery,
    checkResourceExists: (data) =>
      !!data.markets?.find((m) => m.market_id === Number(marketId)),
  });

  const {
    folderStructure,
    filesData,
    filesFlatData,
    loading,
  } = useFileExplorerData({
    filters,
    path,
    scrollContainerRef,
    folderStructureFn: fetchComplianceDocumentsFolderStructure,
    folderStructureQueryKey:
      assetQueryKeys.compliance_documents.folderStructure,
    fetchFilesByFolderFn: fetchComplianceDocumentsByFolder,
    fetchFilesByFolderQueryKey:
      assetQueryKeys.compliance_documents.byFolder,
    fetchFilesFlatFn: fetchComplianceDocuments,
    fetchFilesFlatQueryKey: assetQueryKeys.compliance_documents.flat,
  });

  // The same filter as the list - which always holds the market - so
  // the count and the list cover the same files.
  const documentParams = useMemo(
    () => mapFiltersToParams(filters),
    [filters],
  );

  // A server-side total - the list endpoint returns no count.
  const { data: totalFiles } = useQuery({
    queryKey: [
      ...assetQueryKeys.compliance_documents.folderStructure,
      'total',
      documentParams,
    ],
    queryFn: () =>
      fetchComplianceDocumentsFolderStructure(documentParams),
    select: (data) => data.total_files,
    enabled: !!marketId,
  });

  const refreshDocuments = () =>
    invalidateAssetQueries(queryClient, 'compliance_documents');

  const {
    selected,
    setSelected,
    handleDownloadClick,
    handleRemoveClick,
    handleRemoveCancel,
    handleRemoveConfirm,
    removeModalOpen,
    downloadLoading,
    removeLoading,
  } = useComplianceDocumentBulkActions({
    filesData: filesData as ComplianceDocument[] | undefined,
    filesFlatData: filesFlatData as ComplianceDocument[] | undefined,
    onAfterMutation: refreshDocuments,
  });

  useEffect(() => {
    setSelected([]);
  }, [path, currentView, setSelected]);

  const handleFileDelete = () => {
    refreshDocuments();
  };

  const handleUploadClick = () => {
    setUploadModalOpen(true);
  };

  const handleFileChange = (files: FileList | null) => {
    if (files && files.length > 0) {
      setDroppedFile(files[0]);
      setUploadModalOpen(true);
    }
  };

  const handleFiltersChange = (newFilters: Partial<FilterSchema>) => {
    setFilters({
      ...newFilters,
      market: marketId ? [Number(marketId)] : [],
    });
  };

  const showNotification =
    flattenDeep<
      | string
      | number
      | boolean
      | string[]
      | number[]
      | [string, string]
    >(
      Object.entries(filters || {})
        .filter(([key]) => key !== 'market')
        .map(([, value]) => value),
    ).length > 0;

  return (
    <Root>
      <div
        ref={scrollContainerRef}
        className="page-width-wrapper wide-scrollbar"
      >
        {marketId && (
          <>
            <Breadcrumbs />
            <MarketHeader marketId={marketId} />
            <FileExplorerWrapper>
              <DropZone onFileChange={handleFileChange}>
                <FileExplorer
                  folderStructure={folderStructure}
                  files={filesData as ComplianceDocument[]}
                  filesFlatList={
                    filesFlatData as ComplianceDocument[]
                  }
                  loading={loading}
                  path={path}
                  onPathChange={setPath}
                  type="compliance"
                  onFileDelete={handleFileDelete}
                  showHeader={true}
                  totalCount={totalFiles}
                  selected={selected}
                  onSelect={setSelected}
                  onDownloadClick={handleDownloadClick}
                  onRemoveClick={handleRemoveClick}
                  showDownload={true}
                  showRemove={true}
                  downloadLoading={downloadLoading}
                  removeLoading={removeLoading}
                  currentView={currentView}
                  onViewChange={setCurrentView}
                  actions={
                    <>
                      <Button
                        onClick={handleUploadClick}
                        variant="contained"
                        aria-label="Upload Button"
                      >
                        <UploadIcon />
                        <FormattedMessage
                          id="upload"
                          defaultMessage="Upload"
                        />
                      </Button>
                      <Button
                        variant="outlined"
                        icon
                        ref={filterRef}
                        onClick={() => setFiltersOpen((v) => !v)}
                        aria-label="Filter Button"
                      >
                        <FilterIcon />
                        {showNotification && <FilterNotification />}
                      </Button>
                      <Popper
                        aria-label="Filters Popper"
                        open={filtersOpen}
                        anchorEl={filterRef.current}
                        onClose={() => setFiltersOpen(false)}
                        offsetY={10}
                      >
                        <FileExplorerComplianceFilters
                          filters={filters}
                          onFiltersChange={handleFiltersChange}
                          hiddenSections={['market']}
                          fileTypeConfig={FileTypesComplianceMarket}
                        />
                      </Popper>
                    </>
                  }
                />
              </DropZone>
            </FileExplorerWrapper>
            <RemoveFilesConfirmModal
              open={removeModalOpen}
              count={selected.length}
              loading={removeLoading}
              onCancel={handleRemoveCancel}
              onConfirm={handleRemoveConfirm}
            />
            <ComplianceDocumentsUploadModal
              marketId={Number(marketId)}
              type="Market"
              open={uploadModalOpen}
              onClose={() => {
                setUploadModalOpen(false);
                setDroppedFile(null);
              }}
              onSave={() => {
                setDroppedFile(null);
              }}
              initialFile={droppedFile}
            />
          </>
        )}
      </div>
    </Root>
  );
};

const Root = styled('div')({
  display: 'flex',
  height: '100%',
  background: '#F9FAFB',
  '> *': {
    maxHeight: `calc(100vh - ${NavigationHeight}px)`,
    overflowY: 'auto',
    width: '100%',
  },
});

const FileExplorerWrapper = styled('div')({
  padding: '0 40px',
});

const FilterNotification = styled('div')(({ theme }) => ({
  width: 12,
  height: 12,
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.main,
  position: 'absolute',
  top: -4,
  right: -4,
}));

export default MarketAdminPage;
